import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { t } from 'commons/libs/i18n'
import { toast } from 'react-toastify'
import {
  deleteSalesCalendarApi,
  fetchSalesCalendarList,
  postCreateSalesCalendar,
  postEditSalesCalendar,
  postGetTrackingSalesCalendar,
} from 'services/salesCalendar'
import type {
  SalesCalendarCreateItemPayload,
  SalesCalendarEditItemPayload,
  SalesCalendarEditParams,
  SalesCalendarListItem,
  SalesCalendarTrackingItem,
} from 'services/salesCalendar/interfaces'
import type { AppThunk } from 'store/store'

export interface SalesCalendarListItemUI extends SalesCalendarListItem {
  checked?: boolean
}

type InitialStateType = {
  calendars: SalesCalendarListItemUI[]
  loadingCalendars: boolean
  loadingSaveCalendar: boolean
  pagination: {
    currentPage: number
    pageSize: number
    totalItems: number
  }
  trackingItems: SalesCalendarTrackingItem[]
  loadingTracking: boolean
  trackingPagination: {
    currentPage: number
    pageSize: number
    totalItems: number
  }
}

const initialState: InitialStateType = {
  calendars: [],
  loadingCalendars: true,
  loadingSaveCalendar: false,
  loadingTracking: false,
  pagination: {
    currentPage: 1,
    pageSize: 12,
    totalItems: 0,
  },
  trackingItems: [],
  trackingPagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
  },
}

const salesCalendar = createSlice({
  initialState,
  name: 'salesCalendar',
  reducers: {
    setCalendars(state, action: PayloadAction<SalesCalendarListItemUI[]>) {
      state.calendars = action.payload
    },
    setLoadingCalendars(state, action: PayloadAction<boolean>) {
      state.loadingCalendars = action.payload
    },
    setLoadingSaveCalendar(state, action: PayloadAction<boolean>) {
      state.loadingSaveCalendar = action.payload
    },
    setLoadingTracking(state, action: PayloadAction<boolean>) {
      state.loadingTracking = action.payload
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload
      state.pagination.currentPage = 1
    },
    setTotalItems(state, action: PayloadAction<number>) {
      state.pagination.totalItems = action.payload
    },
    setTrackingItems(state, action: PayloadAction<SalesCalendarTrackingItem[]>) {
      state.trackingItems = action.payload
    },
    setTrackingPage(state, action: PayloadAction<number>) {
      state.trackingPagination.currentPage = action.payload
    },
    setTrackingPageSize(state, action: PayloadAction<number>) {
      state.trackingPagination.pageSize = action.payload
      state.trackingPagination.currentPage = 1
    },
    setTrackingTotalItems(state, action: PayloadAction<number>) {
      state.trackingPagination.totalItems = action.payload
    },
    toggleAllCalendarsChecked(state, action: PayloadAction<boolean>) {
      state.calendars = state.calendars.map((item) => ({
        ...item,
        checked: action.payload,
      }))
    },
    toggleCalendarChecked(state, action: PayloadAction<string>) {
      state.calendars = state.calendars.map((item) => {
        const id = `${item.officeCode}-${item.yearMonth}`
        if (id === action.payload) {
          return { ...item, checked: !item.checked }
        }
        return item
      })
    },
  },
})

export const {
  setLoadingCalendars,
  setLoadingSaveCalendar,
  setPage,
  setPageSize,
  setTotalItems,
  setCalendars,
  toggleCalendarChecked,
  toggleAllCalendarsChecked,
  setTrackingItems,
  setLoadingTracking,
  setTrackingPage,
  setTrackingPageSize,
  setTrackingTotalItems,
} = salesCalendar.actions

export const salesCalendarContext = salesCalendar.reducer

export const getSalesCalendarList =
  (officeCode: string, page: number, perPage: number): AppThunk =>
  async (dispatch) => {
    if (!officeCode) return

    dispatch(setLoadingCalendars(true))

    try {
      const response = await fetchSalesCalendarList({ officeCode, page, perPage })

      dispatch(setCalendars(response.data))
      dispatch(setTotalItems(response.pagination?.totalItems ?? 0))
    } catch (error) {
      toast.error(t('common.errors.unableToFetchData'))
      console.error(error)
    } finally {
      dispatch(setLoadingCalendars(false))
    }
  }

export const createSalesCalendar =
  (payload: SalesCalendarCreateItemPayload, onSuccess?: () => void): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingSaveCalendar(true))

    try {
      const response = await postCreateSalesCalendar(payload)
      if (response.status === 200) {
        toast.success(t('screens.salesCalendar.periodAddSuccess'))
        onSuccess?.()
      } else {
        if (response.data?.Detail) {
          toast.error(response.data.Detail)
        }
      }
    } catch (error) {
      console.error(error)
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingSaveCalendar(false))
    }
  }

export const editSalesCalendar =
  (params: SalesCalendarEditParams, payload: SalesCalendarEditItemPayload): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingSaveCalendar(true))

    try {
      const response = await postEditSalesCalendar(params, payload)
      if (response.status === 200) {
        toast.success(t('screens.salesCalendar.periodEditSuccess'))
        const { currentPage, pageSize } = getState().salesCalendar.pagination
        dispatch(getSalesCalendarList(params.officeCode, currentPage, pageSize))
      } else {
        if (response.data?.Detail) {
          toast.error(response.data.Detail)
        }
      }
    } catch (error) {
      console.error(error)
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingSaveCalendar(false))
    }
  }

export const deleteSalesCalendar =
  (params: SalesCalendarEditParams): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingSaveCalendar(true))

    try {
      const response = await deleteSalesCalendarApi(params)
      if (response.status === 200) {
        toast.success(t('screens.salesCalendar.periodDeleteSuccess'))
        const { currentPage, pageSize } = getState().salesCalendar.pagination
        dispatch(getSalesCalendarList(params.officeCode, currentPage, pageSize))
      } else {
        if (response.data?.Detail) {
          toast.error(response.data.Detail)
        }
      }
    } catch (error) {
      console.error(error)
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingSaveCalendar(false))
    }
  }

export const getSalesCalendarTracking =
  (office: string, yearMonthList: number[], page: number, perPage: number): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingTracking(true))

    try {
      const response = await postGetTrackingSalesCalendar(
        { page, perPage },
        { office, yearMonthList },
      )

      dispatch(setTrackingItems(response.data))
      dispatch(setTrackingTotalItems(response.pagination?.totalItems ?? 0))
    } catch (error) {
      toast.error(t('common.errors.unableToFetchData'))
      console.error(error)
    } finally {
      dispatch(setLoadingTracking(false))
    }
  }
